import React, { useEffect, useRef, useState } from 'react';
import {
  MoreVertical,
  RotateCcw,
  Ban,
  CheckCircle2,
  BellOff,
  Bell
} from 'lucide-react';
import { Conversation, ConversationStatus } from '../../../types/models/whatsapp';

interface ChatOptionsMenuProps {
  conversation: Conversation;
  /** Null while the contact record is still loading. */
  contactBlocked: boolean | null;
  contactOptedOut: boolean;
  onStatusChange: (status: ConversationStatus) => Promise<void>;
  onSetBotEnabled: (enabled: boolean) => Promise<void>;
  onClearBotSession: () => Promise<void>;
  onSetBlocked: (blocked: boolean) => Promise<void>;
  onSetOptedOut: (optedOut: boolean) => Promise<void>;
}

/**
 * Per-conversation controls, mirroring Heltar's "chat options" panel:
 * status, bot state with session reset, and blocking.
 *
 * These are all destructive-ish toggles that an agent reaches for rarely, so
 * they live behind a menu rather than cluttering the header.
 */
const ChatOptionsMenu: React.FC<ChatOptionsMenuProps> = ({
  conversation,
  contactBlocked,
  contactOptedOut,
  onStatusChange,
  onSetBotEnabled,
  onClearBotSession,
  onSetBlocked,
  onSetOptedOut
}) => {
  const [open, setOpen] = useState(false);
  const [busy, setBusy] = useState(false);
  const containerRef = useRef<HTMLDivElement>(null);

  // Close on outside click and on Escape.
  useEffect(() => {
    if (!open) return;

    const onPointerDown = (event: MouseEvent) => {
      if (!containerRef.current?.contains(event.target as Node)) {
        setOpen(false);
      }
    };
    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape') setOpen(false);
    };

    document.addEventListener('mousedown', onPointerDown);
    document.addEventListener('keydown', onKeyDown);
    return () => {
      document.removeEventListener('mousedown', onPointerDown);
      document.removeEventListener('keydown', onKeyDown);
    };
  }, [open]);

  const run = async (action: () => Promise<void>, closeAfter = true) => {
    if (busy) return;
    setBusy(true);
    try {
      await action();
      if (closeAfter) setOpen(false);
    } catch (error) {
      console.error('❌ Chat option failed:', error);
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="flex items-center gap-3">
      {/* Segmented controls for Status and Bot */}
      <div className="flex items-center rounded-md bg-wa-surface p-0.5 border border-wa-border">
        <button
          type="button"
          disabled={busy}
          onClick={() => run(() => onStatusChange('open'), false)}
          className={`px-3 py-1 text-xs font-medium rounded-sm transition-colors ${
            conversation.status === 'open' || conversation.status === 'pending'
              ? 'bg-wa-surface2 text-wa-green shadow-sm'
              : 'text-wa-muted hover:text-wa-text hover:bg-wa-surface2/50'
          }`}
        >
          Open
        </button>
        <button
          type="button"
          disabled={busy}
          onClick={() => run(() => onStatusChange('resolved'), false)}
          className={`px-3 py-1 text-xs font-medium rounded-sm transition-colors ${
            conversation.status === 'resolved'
              ? 'bg-wa-surface2 text-wa-green shadow-sm'
              : 'text-wa-muted hover:text-wa-text hover:bg-wa-surface2/50'
          }`}
        >
          Closed
        </button>
      </div>

      <div className="flex items-center rounded-md bg-wa-surface p-0.5 border border-wa-border">
        <button
          type="button"
          disabled={busy}
          onClick={() => run(() => onSetBotEnabled(true), false)}
          className={`px-3 py-1 text-xs font-medium rounded-sm transition-colors ${
            conversation.botEnabled
              ? 'bg-wa-surface2 text-wa-green shadow-sm'
              : 'text-wa-muted hover:text-wa-text hover:bg-wa-surface2/50'
          }`}
        >
          Bot On
        </button>
        <button
          type="button"
          disabled={busy}
          onClick={() => run(() => onSetBotEnabled(false), false)}
          className={`px-3 py-1 text-xs font-medium rounded-sm transition-colors ${
            !conversation.botEnabled
              ? 'bg-wa-surface2 text-wa-green shadow-sm'
              : 'text-wa-muted hover:text-wa-text hover:bg-wa-surface2/50'
          }`}
        >
          Bot Off
        </button>
      </div>

      <div ref={containerRef} className="relative">
      <button
        type="button"
        onClick={() => setOpen((value) => !value)}
        aria-haspopup="menu"
        aria-expanded={open}
        className="rounded p-1.5 text-gray-500 hover:bg-gray-100"
        aria-label="Chat options"
      >
        <MoreVertical className="h-5 w-5" />
      </button>

      {open && (
        <div
          role="menu"
          className="absolute right-0 top-full z-50 mt-1 w-64 overflow-hidden rounded-lg border border-gray-200 bg-white shadow-xl"
        >
          <Section label="Status" />
          {(['open', 'pending', 'resolved'] as ConversationStatus[]).map(
            (status) => (
              <MenuItem
                key={status}
                label={status}
                capitalize
                checked={conversation.status === status}
                disabled={busy}
                onClick={() => run(() => onStatusChange(status))}
              />
            )
          )}

          <Section label="Bot" />
          <MenuItem
            label="On"
            checked={conversation.botEnabled}
            disabled={busy}
            onClick={() => run(() => onSetBotEnabled(true))}
          />
          <MenuItem
            label="Off"
            checked={!conversation.botEnabled}
            disabled={busy}
            onClick={() => run(() => onSetBotEnabled(false))}
          />
          <button
            type="button"
            role="menuitem"
            disabled={busy}
            onClick={() => run(onClearBotSession)}
            className="flex w-full items-start gap-2 px-3 py-2 text-left hover:bg-gray-50 disabled:opacity-50"
          >
            <RotateCcw className="mt-0.5 h-3.5 w-3.5 shrink-0 text-gray-500" />
            <span>
              <span className="block text-xs text-gray-700">
                Clear bot session
              </span>
              <span className="block text-[11px] text-gray-500">
                Resets bot memory for this contact
              </span>
            </span>
          </button>

          <Section label="Contact" />
          <button
            type="button"
            role="menuitem"
            disabled={busy || contactBlocked === null}
            onClick={() => run(() => onSetBlocked(!contactBlocked))}
            className="flex w-full items-center gap-2 px-3 py-2 text-left hover:bg-gray-50 disabled:opacity-50"
          >
            {contactBlocked ? (
              <CheckCircle2 className="h-3.5 w-3.5 shrink-0 text-wa-green" />
            ) : (
              <Ban className="h-3.5 w-3.5 shrink-0 text-danger-500" />
            )}
            <span className="text-xs text-gray-700">
              {contactBlocked ? 'Unblock contact' : 'Block contact'}
            </span>
          </button>

          <button
            type="button"
            role="menuitem"
            disabled={busy}
            onClick={() => run(() => onSetOptedOut(!contactOptedOut))}
            className="flex w-full items-start gap-2 px-3 py-2 text-left hover:bg-gray-50 disabled:opacity-50"
          >
            {contactOptedOut ? (
              <Bell className="mt-0.5 h-3.5 w-3.5 shrink-0 text-wa-green" />
            ) : (
              <BellOff className="mt-0.5 h-3.5 w-3.5 shrink-0 text-gray-500" />
            )}
            <span>
              <span className="block text-xs text-gray-700">
                {contactOptedOut ? 'Mark as opted in' : 'Mark as opted out'}
              </span>
              <span className="block text-[11px] text-gray-500">
                {contactOptedOut
                  ? 'Allows outbound messages again'
                  : 'Blocks all outbound messages'}
              </span>
            </span>
          </button>
        </div>
      )}
    </div>
    </div>
  );
};

const Section: React.FC<{ label: string }> = ({ label }) => (
  <p className="border-t border-gray-100 bg-gray-50 px-3 py-1.5 text-[11px] font-semibold uppercase tracking-wide text-gray-500 first:border-t-0">
    {label}
  </p>
);

const MenuItem: React.FC<{
  label: string;
  checked: boolean;
  disabled: boolean;
  capitalize?: boolean;
  onClick: () => void;
}> = ({ label, checked, disabled, capitalize, onClick }) => (
  <button
    type="button"
    role="menuitemradio"
    aria-checked={checked}
    disabled={disabled}
    onClick={onClick}
    className={`flex w-full items-center justify-between px-3 py-1.5 text-left text-xs hover:bg-gray-50 disabled:opacity-50 ${
      capitalize ? 'capitalize' : ''
    } text-gray-700`}
  >
    {label}
    {checked && <CheckCircle2 className="h-3.5 w-3.5 text-primary-600" />}
  </button>
);

export default ChatOptionsMenu;
